import React, { HTMLInputTypeAttribute } from 'react';
import useInputValidation, { IInputValitationResponse } from '../../../hooks/input-validation-hook';
import { IValidationGroup } from '../../../hooks/validation-group/validation-group-hook';
import { IconProps } from '../../icons/icon-props-interface';
import Icon from '../../icons/icon/icon';
import './input.scss';
import InputErrorMessages from '../input-error-messages/input-error-messages';

export interface IInvalidCharactersResult {
	isValid: boolean;
	message?: string;
}

export interface IValidationProps {
    required?: boolean;
    validationGroup?: IValidationGroup;
    validationCallback?: () => IInputValitationResponse; 
    showValidationMessage?: boolean;
}

interface IInputProps extends IValidationProps {
	type?: HTMLInputTypeAttribute,
	placeholder?: string,
	value?: string|number|null;
	onChange?: (value: any) => void,
	onSubmit?: () => void;
	min?: number;
	max?: number;
	minlength?: number;
	maxlength?: number;
	disabled?: boolean;
	preventCharacters?: (input: string) => IInvalidCharactersResult;
	button?: {
		iconComp: React.FC<IconProps>,
		onClick?: () => void;
	};
	adornment?: React.ReactNode,
	autoComplete?: string,
	autoFocus?: boolean,
	step?: number,
	/** added here https://returngo.atlassian.net/browse/RD-5329 to fix bug in numeric input to allow delete value (default behavior always keeps 0)
	 *  - temp=true intructs the component to call onchange with null value for numeric input when empty
	 * must be deleted when all inputs of type number will use temp=true */
	temp?: boolean; /** maybe we want to add prop nullable:bool so when input is empty it will call onChange with null */
}

export const Input: React.FC<IInputProps> = ({
	type = 'text', value, placeholder, onChange, onSubmit,
	preventCharacters,
	min, max, disabled,
	button, adornment,
	minlength, maxlength, autoComplete = 'off',
	autoFocus = false,
	step, temp = false,
    required, validationGroup, validationCallback, showValidationMessage,
}) => {    
	const { isValid, messages } = useInputValidation({ value, validation: { required, validationGroup, validationCallback, showValidationMessage } });

	const handleKeyPress = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter') {
			if (onSubmit) {
				onSubmit();
			}
		}
	}
	
	const handleKeyDown= (e:React.KeyboardEvent)=>{
		if (preventCharacters) {
			const {isValid} = preventCharacters(e.key);
			if (!isValid) {
				e.preventDefault();
			}
		}
	}

	const handleOnChange = (e:React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		/** handle numeric input max value, native not working */
		if (type === 'number') {

			/** special case for number input to allow empty input */
			if (temp && value.trim() === '') {
				return onChange && onChange(null);
			}

			const numValue = Number(value);
			if (isNaN(numValue)) {
				return;
			}

			if (min !== undefined && numValue < min) {
				return;
			}

			if (max !== undefined && numValue > max) {
				return;
			}
			return onChange && onChange(numValue);
		}
		onChange && onChange(value);
	}
	
	function handleOnPaste(e: React.ClipboardEvent) {
		if (preventCharacters) {
			const {isValid} = preventCharacters(e.clipboardData.getData('Text'));
			if (!isValid) {
				e.preventDefault();
			}
		}
	}
	
	return(
        <>
		<div className={`input${ adornment ? ' input-adornment' : ''} ${ button ? ' input-button' : ''}`}>

			{adornment &&
				<div className='adornment'>
					{adornment}
				</div>
			}

			<input className={`${ !isValid ? 'invalid' : ''}`}
				min={min} max={max} type={type} placeholder={placeholder}
				minLength={minlength} maxLength={maxlength}
				onChange={handleOnChange}
				onKeyPress={handleKeyPress}
		        onKeyDown={handleKeyDown}
			    onPaste={handleOnPaste}
				value={value ?? ''}
				disabled={disabled}
				step={step ?? 1}
				autoComplete={autoComplete ?? undefined}
				autoFocus={autoFocus}
			/>

			{button &&
				<div className={`input-icon ${button.onClick ? 'clickable' : ''}`} onClick={button.onClick}>
					<Icon Component={button.iconComp} />
				</div>
			}
		</div>
        <InputErrorMessages messages={messages} />
        </>
	)
};
